import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { AutoAwesome, Refresh } from '@mui/icons-material';
import { AuditAnalysis, AuditSession, AuditSeries, aiBuilderAPI } from '../services/api';
import LoadingModal from './LoadingModal';

interface AIInsightsGeneratorProps {
  auditAnalyses: Record<number, AuditAnalysis>;
  auditSessions: AuditSession[];
  auditSeries: Record<number, string>;
  auditSeriesData: Record<number, AuditSeries>;
  companyName: string;
  companyId: number;
  siteId?: number;  // Optional: for site-specific commentary
  siteName?: string;
}

const AIInsightsGenerator: React.FC<AIInsightsGeneratorProps> = ({
  auditAnalyses,
  auditSessions,
  auditSeries,
  auditSeriesData,
  companyName,
  companyId,
  siteId,
  siteName,
}) => {
  const siteContext = siteId ? `site ${siteId} (${siteName})` : 'all sites';
  console.log('🔄 AIInsightsGenerator: Component rendered for company:', companyId, siteContext);
  const [insights, setInsights] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false); // For manual regeneration
  const [error, setError] = useState('');
  const [cacheInfo, setCacheInfo] = useState<{
    auditsAnalyzed: number;
    newlyGenerated: number;
    cachedUsed: number;
  } | null>(null);
  const [lastGeneratedForAudits, setLastGeneratedForAudits] = useState<Set<number>>(new Set());
  const [hasInitiallyGenerated, setHasInitiallyGenerated] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  
  // Use a ref to prevent duplicate calls even across re-renders
  const callInProgressRef = useRef(false);

  const generateInsights = async (forceRegenerate = false) => {
    // Use regenerating state for manual regenerations, loading for automatic
    if (forceRegenerate) {
      setRegenerating(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const siteContext = siteId ? `site ${siteId}` : 'all sites';
      console.log('🤖 Calling AI commentary API for company:', companyId, siteContext, forceRegenerate ? '(force regenerate)' : '');
      const response = await aiBuilderAPI.generateCommentary(companyId, forceRegenerate, siteId);
      
      if (response.success) {
        setInsights(response.commentary);
        setHasInitiallyGenerated(true); // Mark that we've generated insights
        setCacheInfo({
          auditsAnalyzed: response.audits_analyzed || 0,
          newlyGenerated: response.newly_generated || 0,
          cachedUsed: response.cached_used || 0,
        });
        console.log('✅ AI commentary generated successfully for', siteContext);
      } else {
        throw new Error(response.message || 'Failed to generate commentary');
      }
    } catch (err: any) {
      console.error('Error generating AI insights:', err);
      setError(err.message || 'Failed to generate insights. Please try again.');
    } finally {
      if (forceRegenerate) {
        setRegenerating(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Track last site to detect changes
  const lastSiteIdRef = useRef<number | undefined>(siteId);

  // Reset state when site changes
  useEffect(() => {
    if (lastSiteIdRef.current !== siteId) {
      console.log('🔄 AIInsightsGenerator: Site changed from', lastSiteIdRef.current, 'to', siteId);
      lastSiteIdRef.current = siteId;
      // Reset state to trigger new generation
      setHasInitiallyGenerated(false);
      setInsights('');
      setCacheInfo(null);
      setLastGeneratedForAudits(new Set());
    }
  }, [siteId]);

  // Initialize insights loading when data is ready or site changes
  useEffect(() => {
    if (!companyId || Object.keys(auditAnalyses).length === 0 || auditSessions.length === 0 || isInitializing || hasInitiallyGenerated || callInProgressRef.current) return;
    
    const siteContext = siteId ? `site ${siteId}` : 'all sites';
    console.log('🔍 AIInsightsGenerator: Starting initialization for company:', companyId, siteContext);
    
    // Set guards to prevent duplicate calls
    setIsInitializing(true);
    callInProgressRef.current = true;
    
    // Try to get cached insights without forcing regeneration
    aiBuilderAPI.generateCommentary(companyId, false, siteId)
      .then(response => {
        console.log('🔍 AIInsightsGenerator: Received response:', {
          success: response.success,
          hasCommentary: !!response.commentary,
          cachedUsed: response.cached_used,
          newlyGenerated: response.newly_generated,
          siteId: response.site_id
        });
        
        if (response.success && response.commentary) {
          setInsights(response.commentary);
          setCacheInfo({
            auditsAnalyzed: response.audits_analyzed || 0,
            newlyGenerated: response.newly_generated || 0,
            cachedUsed: response.cached_used || 0,
          });
          
          if (response.cached_used > 0) {
            console.log('✅ AIInsightsGenerator: Loaded cached insights for', siteContext);
          } else {
            console.log('🤖 AIInsightsGenerator: Generated new insights for', siteContext);
          }
          
          // Mark all current audits as processed
          const currentAuditIds = new Set(auditSessions.map(session => session.id));
          setLastGeneratedForAudits(currentAuditIds);
        }
        setHasInitiallyGenerated(true);
      })
      .catch(err => {
        console.error('❌ AIInsightsGenerator: Error loading insights:', err);
        setError('Failed to load insights');
        setHasInitiallyGenerated(true);
      })
      .finally(() => {
        setIsInitializing(false);
        callInProgressRef.current = false;
        console.log('🏁 AIInsightsGenerator: Initialization complete for', siteContext);
      });
  }, [companyId, Object.keys(auditAnalyses).length, auditSessions.length, siteId, hasInitiallyGenerated]);

  // Check for new audits after initial load
  useEffect(() => {
    if (Object.keys(auditAnalyses).length === 0 || !companyId || !hasInitiallyGenerated) return;

    // Get current audit session IDs
    const currentAuditIds = new Set(auditSessions.map(session => session.id));
    
    // Check if there are new audits that we haven't generated insights for
    const hasNewAudits = Array.from(currentAuditIds).some(id => !lastGeneratedForAudits.has(id));
    
    if (hasNewAudits) {
      console.log('🆕 New audits detected, generating insights');
      generateInsights().then(() => {
        // Update the tracking set to include all current audits
        setLastGeneratedForAudits(currentAuditIds);
      });
    } else {
      console.log('✅ No new audits detected');
    }
  }, [auditSessions.length, hasInitiallyGenerated]); // Only check when audit count changes and after initial load

  // Helper to get score color based on percentage
  const getScoreColor = (score: number): string => {
    if (score >= 75) return '#4caf50'; // Green
    if (score >= 50) return '#ff9800'; // Amber/Orange
    return '#f44336'; // Red
  };

  // Helper to extract score from text like "(Overall Score: 59.1%)" or "Average Score: 59.1%"
  const extractScore = (text: string): number | null => {
    const match = text.match(/(\d+\.?\d*)%/);
    return match ? parseFloat(match[1]) : null;
  };

  // Parse and render insights text with proper formatting
  const renderInsights = (text: string) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    
    // Track if we're inside an audit card
    let currentAuditCard: {
      title: string;
      score: number | null;
      sites?: string;
      content: React.ReactNode[];
    } | null = null;
    
    // Helper to flush current audit card to elements
    const flushAuditCard = () => {
      if (currentAuditCard) {
        const scoreColor = currentAuditCard.score ? getScoreColor(currentAuditCard.score) : '#1976d2';
        elements.push(
          <Card 
            key={`audit-card-${elements.length}`}
            sx={{ 
              mb: 3, 
              borderLeft: `4px solid ${scoreColor}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              }
            }}
          >
            <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
              {/* Audit Header */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                    {currentAuditCard.title}
                  </Typography>
                  {currentAuditCard.sites && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      📍 {currentAuditCard.sites}
                    </Typography>
                  )}
                </Box>
                {currentAuditCard.score !== null && (
                  <Chip 
                    label={`${currentAuditCard.score.toFixed(1)}%`}
                    sx={{ 
                      bgcolor: scoreColor,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      height: 32,
                      minWidth: 70,
                    }}
                  />
                )}
              </Box>
              
              {/* Audit Content */}
              <Box sx={{ pl: 0 }}>
                {currentAuditCard.content}
              </Box>
            </CardContent>
          </Card>
        );
        currentAuditCard = null;
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      if (!trimmedLine) {
        if (currentAuditCard) {
          currentAuditCard.content.push(<Box key={`space-${index}`} sx={{ height: '8px' }} />);
        } else {
          elements.push(<Box key={index} sx={{ height: '8px' }} />);
        }
        return;
      }

      // Detect AUDIT TYPE: header (start of a new audit card)
      if (trimmedLine.startsWith('AUDIT TYPE:') || trimmedLine.startsWith('AUDIT:')) {
        // Flush any existing card first
        flushAuditCard();
        
        // Extract title (remove "AUDIT TYPE:" or "AUDIT:" prefix)
        const title = trimmedLine.replace(/^AUDIT( TYPE)?:\s*/i, '').trim();
        const score = extractScore(title);
        
        // Clean the title (remove score portion if present)
        const cleanTitle = title.replace(/\s*\(Overall Score:.*?\)/, '').replace(/\s*\(Average Score:.*?\)/, '').trim();
        
        currentAuditCard = {
          title: cleanTitle,
          score: score,
          content: []
        };
        return;
      }

      // Detect "Sites Completed:" line
      if (trimmedLine.startsWith('Sites Completed:') && currentAuditCard) {
        currentAuditCard.sites = trimmedLine.replace('Sites Completed:', '').trim();
        return;
      }

      // Detect "Average Score:" or "Overall Score:" line (if score wasn't in header)
      if ((trimmedLine.startsWith('Average Score') || trimmedLine.startsWith('Overall Score')) && currentAuditCard) {
        const score = extractScore(trimmedLine);
        if (score !== null) {
          currentAuditCard.score = score;
        }
        return;
      }

      // Handle section headers (lines that end with : and are standalone)
      if (trimmedLine.endsWith(':') && !trimmedLine.startsWith('-') && !trimmedLine.startsWith('•')) {
        // Check if this is a sub-header within an audit card
        const isSubHeader = trimmedLine.toLowerCase().includes('weak areas') || 
                           trimmedLine.toLowerCase().includes('strong areas') ||
                           trimmedLine.toLowerCase().includes('average scores') ||
                           trimmedLine.toLowerCase().includes('category performance');
        
        const isInsightHeader = trimmedLine.toLowerCase().includes('insight');
        
        if (currentAuditCard && isSubHeader) {
          currentAuditCard.content.push(
            <Typography key={`subheader-${index}`} variant="subtitle2" sx={{ 
              mt: 1.5, 
              mb: 0.5, 
              fontWeight: 'bold', 
              color: 'error.main'
            }}>
              {trimmedLine}
            </Typography>
          );
        } else {
          // Flush any existing card before a main section header
          flushAuditCard();
          
          elements.push(
            <Typography key={index} variant="h6" sx={{ 
              mt: 2, 
              mb: 1, 
              fontWeight: 'bold', 
              color: isInsightHeader ? 'error.main' : 'primary.main' 
            }}>
              {trimmedLine}
            </Typography>
          );
        }
        return;
      }

      // Handle bullet points or lines starting with dashes
      if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('• ')) {
        const bulletContent = (
          <Typography key={`bullet-${index}`} variant="body2" sx={{ mb: 0.5, ml: 2, lineHeight: 1.6, color: 'text.secondary' }}>
            {trimmedLine}
          </Typography>
        );
        
        if (currentAuditCard) {
          currentAuditCard.content.push(bulletContent);
        } else {
          elements.push(bulletContent);
        }
        return;
      }

      // Handle lines that start with "Insight:" (even if not standalone)
      if (trimmedLine.toLowerCase().startsWith('insight:')) {
        const parts = trimmedLine.split(/^(insight:)/i);
        if (parts.length >= 3) {
          const insightContent = (
            <Typography key={`insight-${index}`} variant="body1" sx={{ mb: 1, lineHeight: 1.6 }}>
              <Typography component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                {parts[1]}
              </Typography>
              <Typography component="span">
                {parts[2]}
              </Typography>
            </Typography>
          );
          
          if (currentAuditCard) {
            currentAuditCard.content.push(insightContent);
          } else {
            elements.push(insightContent);
          }
          return;
        }
      }

      // Regular text lines
      const textContent = (
        <Typography key={`text-${index}`} variant="body2" sx={{ mb: 1, lineHeight: 1.7, color: 'text.primary' }}>
          {trimmedLine}
        </Typography>
      );
      
      if (currentAuditCard) {
        currentAuditCard.content.push(textContent);
      } else {
        elements.push(textContent);
      }
    });

    // Flush any remaining audit card
    flushAuditCard();

    return <Box>{elements}</Box>;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Generating AI insights...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!insights) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No insights available yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AutoAwesome sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle2" color="primary.main">
            AI-Generated Insights & Commentary
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {cacheInfo && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {cacheInfo.newlyGenerated > 0 && (
                <Chip 
                  label={`${cacheInfo.newlyGenerated} new`} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              )}
              {cacheInfo.cachedUsed > 0 && (
                <Chip 
                  label={`${cacheInfo.cachedUsed} cached`} 
                  size="small" 
                  color="success" 
                  variant="outlined"
                />
              )}
            </Box>
          )}
          
          <Tooltip title="Regenerate all commentary">
            <IconButton 
              size="small" 
              onClick={() => generateInsights(true)}
              disabled={loading || regenerating}
              sx={{ ml: 1 }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {renderInsights(insights)}
      
      {/* Loading Modal for Regeneration */}
      <LoadingModal
        open={regenerating}
        title="Regenerating Commentary"
        message="AI is analyzing all audit data to generate fresh insights. This may take several seconds."
      />
    </Box>
  );
};

export default AIInsightsGenerator; 